import React, { useState, useEffect } from 'react';
import { X, Plus, Search, Edit, Trash2, RefreshCw, BookOpen, AlertCircle } from 'lucide-react';
import { useOrganization, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';

function GlossaryModal({ isOpen, onClose, user, selectedWorkspace }) {
  const { organization } = useOrganization();
  const clerk = useClerk();
  const [terms, setTerms] = useState([]);
  const [filteredTerms, setFilteredTerms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total_terms: 0 });
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    term: '',
    definition: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Load terms when modal opens
  useEffect(() => {
    if (isOpen && organization && clerk?.session) {
      loadTerms();
      loadStats();
      resetForm();
    }
  }, [isOpen, organization, clerk]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter terms based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTerms(terms);
    } else {
      const filtered = terms.filter(term => 
        term.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        term.definition.toLowerCase().includes(searchQuery.toLowerCase()) ||
        term.context.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTerms(filtered);
    }
  }, [searchQuery, terms]);

  const loadTerms = async () => {
    if (!organization || !clerk?.session) return;

    if (!selectedWorkspace?.workspace_id) {
      setError('No workspace selected. Please select a workspace first.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setError('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const workspaceId = selectedWorkspace.workspace_id;
      const response = await fetch(`${apiBaseUrl}/glossary/?workspace_id=${workspaceId}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      if (data.success) {
        setTerms(data.terms);
      } else {
        setError('Failed to load glossary terms');
      }
    } catch (err) {
      setError('Error loading glossary terms: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    if (!organization || !clerk?.session) return;
    if (!selectedWorkspace?.workspace_id) return;

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        console.error('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const workspaceId = selectedWorkspace.workspace_id;
      const response = await fetch(`${apiBaseUrl}/glossary/info/stats?workspace_id=${workspaceId}`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading glossary stats:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      term: '',
      definition: ''
    });
    setSelectedTerm(null);
    setIsEditing(false);
  };

  const handleEdit = (term) => {
    setFormData({
      term: term.term,
      definition: term.definition
    });
    setSelectedTerm(term);
    setIsEditing(true);
  };

  const handleSelectTerm = (term) => {
    setSelectedTerm(term);
    setIsEditing(false);
  };

  const handleDelete = async (term) => {
    if (!window.confirm(`Are you sure you want to delete the term "${term.term}"?`)) {
      return;
    }

    if (!selectedWorkspace?.workspace_id) {
      setError('No workspace selected. Please select a workspace first.');
      return;
    }

    if (!organization || !clerk?.session) {
      setError('Unable to get authentication');
      return;
    }

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setError('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const workspaceId = selectedWorkspace.workspace_id;
      const response = await fetch(`${apiBaseUrl}/glossary/${term.term}?workspace_id=${workspaceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      if (data.success) {
        setTerms(prev => prev.filter(t => t.term !== term.term));
        loadStats();
      } else {
        setError('Failed to delete term');
      }
    } catch (err) {
      setError('Error deleting term: ' + err.message);
    }
  };

  const handleInputChange = (field, value) => {
    // Allow spaces for multi-word terms, but normalize whitespace during typing
    if (field === 'term') {
      // Only collapse multiple consecutive spaces into single space (but keep the space!)
      // Don't trim during typing - trim only on blur/save
      value = value.replace(/\s+/g, ' ');
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTermBlur = () => {
    // Trim only when user leaves the field
    setFormData(prev => ({
      ...prev,
      term: prev.term.trim()
    }));
  };

  const validateForm = () => {
    if (!formData.term.trim()) {
      setError('Term is required');
      return false;
    }
    // Allow multi-word terms, just ensure no leading/trailing spaces
    if (formData.term !== formData.term.trim()) {
      setError('Term cannot have leading or trailing spaces');
      return false;
    }
    if (!formData.definition.trim()) {
      setError('Definition is required');
      return false;
    }
    return true;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!selectedWorkspace?.workspace_id) {
      setError('No workspace selected. Please select a workspace first.');
      return;
    }

    if (!organization || !clerk?.session) {
      setError('Unable to get authentication');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        setError('Unable to get authentication token');
        return;
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const workspaceId = selectedWorkspace.workspace_id;
      const url = selectedTerm && isEditing
        ? `${apiBaseUrl}/glossary/${selectedTerm.term}?workspace_id=${workspaceId}`
        : `${apiBaseUrl}/glossary/?workspace_id=${workspaceId}`;
      
      const method = selectedTerm && isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          term: formData.term,
          definition: formData.definition,
          context: '',
          examples: []
        })
      });

      const data = await response.json();

      if (data.success) {
        if (selectedTerm && isEditing) {
          setTerms(prev => prev.map(t => t.term === selectedTerm.term ? data.term : t));
          setSelectedTerm(data.term);
        } else {
          setTerms(prev => [...prev, data.term]);
          setSelectedTerm(data.term);
        }
        loadStats();
        setIsEditing(false);
      } else {
        setError(data.detail || 'Failed to save term');
      }
    } catch (err) {
      setError('Error saving term: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content glossary-modal-ultra-compact" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-compact">
          <div className="modal-title-compact">
            <BookOpen size={16} />
            <h3>Glossary ({stats.total_terms})</h3>
          </div>
          <div className="modal-actions-compact">
            <button 
              className="refresh-btn-small" 
              onClick={loadTerms}
              title="Refresh"
              disabled={isLoading}
            >
              <RefreshCw size={14} />
            </button>
            <button 
              className="close-btn-small" 
              onClick={handleClose}
              disabled={isSaving}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="modal-content-body">
          {/* Left Panel - Terms List */}
          <div className="left-panel">
            <div className="search-section-compact">
              <div className="search-input-container-compact">
                <Search className="search-icon" size={14} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input-compact"
                />
              </div>
            </div>

            <div className="terms-list-container">
              {error && (
                <div className="error-message">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}

              {isLoading ? (
                <div className="loading-message">
                  Loading glossary terms...
                </div>
              ) : filteredTerms.length === 0 ? (
                <div className="empty-state">
                  {searchQuery ? (
                    <div>
                      <p>No terms found matching "{searchQuery}"</p>
                      <button 
                        className="clear-search-btn" 
                        onClick={() => setSearchQuery('')}
                      >
                        Clear Search
                      </button>
                    </div>
                  ) : (
                    <div>
                      <BookOpen size={32} className="empty-icon" />
                      <p>No glossary terms yet</p>
                      <p className="empty-subtitle">Add your first term below</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="terms-list-compact">
                  {filteredTerms.map((term, index) => (
                    <div 
                      key={term.term} 
                      className={`term-item-compact ${selectedTerm?.term === term.term ? 'selected' : ''}`}
                      onClick={() => handleSelectTerm(term)}
                    >
                      <div className="term-content-compact">
                        <span className="term-name-compact">{term.term}</span>
                        <span className="term-definition-compact">{term.definition}</span>
                      </div>
                      <div className="term-actions-compact" onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="edit-btn-small" 
                          onClick={() => handleEdit(term)}
                          title="Edit"
                        >
                          <Edit size={12} />
                        </button>
                        <button 
                          className="delete-btn-small" 
                          onClick={() => handleDelete(term)}
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Always Show Add/Edit Form */}
          <div className="right-panel">
            <div className="form-section-compact">
              <div className="form-header-compact">
                <h4>{selectedTerm ? 'Edit' : 'Add'}</h4>
                {selectedTerm && (
                  <button 
                    className="cancel-edit-btn-small" 
                    onClick={() => {
                      setSelectedTerm(null);
                      resetForm();
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                )}
              </div>

              <div className="glossary-info-box">
                <AlertCircle size={16} className="info-icon" />
                <p>When any glossary term is detected in your message, it's automatically included for processing. The agent will never forget these terms.</p>
              </div>

              <form onSubmit={handleSave} className="glossary-form-ultra-compact">
                <div className="form-group">
                  <label htmlFor="term">Term * <span className="field-hint">(single or multi-word)</span></label>
                  <input
                    id="term"
                    type="text"
                    value={formData.term}
                    onChange={(e) => handleInputChange('term', e.target.value)}
                    onBlur={handleTermBlur}
                    placeholder="Enter term (e.g., 'sales quota' or 'aircraft parts')"
                    disabled={isSaving}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="definition">Definition *</label>
                  <textarea
                    id="definition"
                    value={formData.definition}
                    onChange={(e) => handleInputChange('definition', e.target.value)}
                    placeholder="Enter the definition"
                    rows={4}
                    disabled={isSaving}
                    required
                  />
                </div>

                {error && (
                  <div className="error-message">
                    <AlertCircle size={16} />
                    {error}
                  </div>
                )}

                <div className="form-actions">
                  <button 
                    type="submit" 
                    disabled={isSaving}
                    className="save-btn"
                  >
                    {isSaving ? (
                      <>
                        <div className="spinner"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        {selectedTerm ? 'Update Term' : 'Add Term'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GlossaryModal;